import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatDuration,
  formatPace,
  summariseCardio,
  type CardioMachineKind,
  type CardioSegment,
} from '@liftx/core';
import { sessionRepository, type CardioSegmentInput } from '../../data/repositories/sessionRepository';
import { exerciseRepository } from '../../data/repositories/exerciseRepository';
import { formatExerciseName } from '../../ui/format/exerciseName';
import { ExerciseThumbnail } from '../../ui/components/ExerciseThumbnail';
import { ExerciseInfoDetails } from '../exercises/ExerciseInfoDetails';
import { CardioSegmentRow } from './CardioSegmentRow';
import type { CardioSegmentRecord, ExerciseRecord, SessionExerciseRecord } from '../../data/types';
import styles from './Workout.module.css';

interface PrescriptionSnapshotSet {
  setIndex: number;
  targetDurationS: number | null;
  targetDistanceM: number | null;
}

/**
 * What the plan is asking for, read from `prescriptionSnapshot` (C3).
 *
 * The target lives there and only there — it is deliberately never copied
 * onto `CardioSegmentRecord`, so there is no second copy to drift.
 */
function targetFor(exercise: SessionExerciseRecord): { durationS: number | null; distanceM: number | null } | null {
  const snapshot = exercise.prescriptionSnapshot as { sets?: PrescriptionSnapshotSet[] } | null;
  const sets = snapshot?.sets;
  if (sets === undefined || sets.length === 0) return null;

  // Every row of a uniform cardio prescription carries the same target; a
  // per-set ladder is summed, so "3 × 400 m" reads as 1200 m rather than
  // showing only the first leg.
  const durationS = sets.reduce<number | null>(
    (total, set) => (set.targetDurationS === null ? total : (total ?? 0) + set.targetDurationS),
    null,
  );
  const distanceM = sets.reduce<number | null>(
    (total, set) => (set.targetDistanceM === null ? total : (total ?? 0) + set.targetDistanceM),
    null,
  );
  if (durationS === null && distanceM === null) return null;
  return { durationS, distanceM };
}

/** Dexie row → the shape `@liftx/core`'s cardio functions expect. */
function toCardioSegment(row: CardioSegmentRecord): CardioSegment {
  return {
    id: row.id,
    sessionExerciseId: row.sessionExerciseId,
    segmentIndex: row.orderIndex,
    durationS: row.durationS,
    distanceM: row.distanceM,
    calories: null,
    avgHr: row.avgHeartRate,
    maxHr: null,
    inclinePct: row.inclinePct ?? null,
    resistanceLevel: row.resistanceLevel ?? null,
    isCompleted: row.isCompleted,
  };
}

/**
 * Which machine-Level convention (if any) this exercise's name matches —
 * Level → estimated speed feature. Deliberately name-based rather than a new
 * catalogue field: the catalogue itself is out of scope for this feature, and
 * a simple keyword match is enough to find the treadmill/bike exercises
 * actually in it ("walking on incline treadmill", "stationary bike run",
 * "cycle cross trainer", …) without guessing at exercises a Level dial does
 * not apply to (a plain "run", "burpee", "jump rope" — no `null` mapping
 * needed there because none of those keywords match).
 */
function cardioMachineKind(exerciseName: string): CardioMachineKind | null {
  const name = exerciseName.toLowerCase();
  if (name.includes('treadmill')) return 'treadmill';
  if (name.includes('bike') || name.includes('cycl')) return 'bike';
  return null;
}

/**
 * One cardio exercise within the workout — Cardio Phase C4.
 *
 * The sibling of `ExercisePanel`, not a replacement for it: same panel shell,
 * same header, same expand/collapse, same info button, same remove control.
 * What differs is the body, because cardio logs segments (C2/C3) rather than
 * sets, and a cardio session exercise has zero `sessionSets` by design.
 *
 * Every derived number here comes from `@liftx/core`'s cardio functions
 * (`summariseCardio`, `paceSecondsPerKm`, `speedKph`, `formatPace`). No pace
 * or speed arithmetic is reimplemented in the UI, and none of it is stored.
 */
export function CardioPanel({
  exercise,
  index,
  expanded,
  onToggleExpand,
  onSegmentCompleted,
  onRemove,
}: {
  exercise: SessionExerciseRecord;
  index: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onSegmentCompleted: () => void;
  /** Asks the screen to confirm and remove this exercise from the workout. */
  onRemove: () => void;
}): React.JSX.Element {
  const [segments, setSegments] = useState<CardioSegmentRecord[]>([]);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoExercise, setInfoExercise] = useState<ExerciseRecord | null>(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [thumbnailExercise, setThumbnailExercise] = useState<ExerciseRecord | null>(null);

  /**
   * Guards "+ Add segment" against a double tap.
   *
   * A ref checked and set synchronously, not a `disabled` flag: `addSegment`
   * is asynchronous, so two taps dispatched before React re-renders would
   * both get through a state-only guard and create two segments. The same
   * approach `ExercisePickerDialog` already uses, and deliberately at the UI
   * layer — C2's report recorded that the repository does not (and should
   * not) carry an idempotency framework for adds.
   */
  const addingRef = useRef(false);

  const refresh = useCallback(async () => {
    setSegments(await sessionRepository.listSegments(exercise.id));
  }, [exercise.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (exercise.exerciseId === null) {
      setThumbnailExercise(null);
      return;
    }
    let cancelled = false;
    setThumbnailExercise(null);
    void exerciseRepository.get(exercise.exerciseId).then((row) => {
      if (!cancelled) setThumbnailExercise(row ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [exercise.exerciseId]);

  // Fetched only when the info panel is actually opened, matching
  // `ExercisePanel` — most panels on screen will never be opened.
  useEffect(() => {
    if (!infoOpen || exercise.exerciseId === null) return;
    let cancelled = false;
    setInfoLoading(true);
    void exerciseRepository.get(exercise.exerciseId).then((row) => {
      if (!cancelled) {
        setInfoExercise(row ?? null);
        setInfoLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [infoOpen, exercise.exerciseId]);

  async function handleChange(segmentId: string, values: CardioSegmentInput): Promise<void> {
    await sessionRepository.updateSegment(segmentId, values);
    await refresh();
  }

  async function handleComplete(segmentId: string, values: CardioSegmentInput): Promise<void> {
    await sessionRepository.completeSegment(segmentId, values);
    await refresh();
    onSegmentCompleted();
  }

  async function handleUncomplete(segmentId: string): Promise<void> {
    await sessionRepository.uncompleteSegment(segmentId);
    await refresh();
  }

  async function handleAddSegment(): Promise<void> {
    if (addingRef.current) return;
    addingRef.current = true;
    try {
      await sessionRepository.addSegment(exercise.id);
      await refresh();
    } finally {
      addingRef.current = false;
    }
  }

  const summary = summariseCardio(segments.map(toCardioSegment));
  const target = targetFor(exercise);
  const exerciseName = formatExerciseName(exercise.exerciseNameSnapshot);
  const machineKind = cardioMachineKind(exercise.exerciseNameSnapshot);

  return (
    <div className={styles.exercisePanel}>
      <div className={styles.exerciseHeader}>
        <button type="button" className={styles.exerciseHeaderMain} onClick={onToggleExpand}>
          {thumbnailExercise !== null && <ExerciseThumbnail exercise={thumbnailExercise} size={112} />}
          <span className={styles.exerciseHeaderText}>
            <span className={styles.exerciseName}>
              {exerciseName}
            </span>
            {/* Segments, never "0/0 sets" — a cardio exercise legitimately has
                no sets, and reporting them was the cosmetic defect C3's audit
                logged against `ExercisePanel` for this phase to fix. */}
            <span className={styles.exerciseMeta}>
              {summary.completedSegments}/{segments.length} segments
              {summary.totalDistanceM > 0 && ` · ${(summary.totalDistanceM / 1000).toFixed(2)} km`}
            </span>
          </span>
        </button>
        <button
          type="button"
          className={styles.infoButton}
          aria-expanded={infoOpen}
          aria-label={`${infoOpen ? 'Hide' : 'Show'} exercise information for ${exerciseName}`}
          onClick={() => setInfoOpen((value) => !value)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="10" cy="6.5" r="1" fill="currentColor" />
            <path d="M10 9.5V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {infoOpen && (
        <div className={styles.infoPanel}>
          {infoLoading && (
            <p role="status" className={styles.stateText}>
              Loading…
            </p>
          )}
          {!infoLoading && infoExercise === null && (
            <p className={styles.stateText}>Exercise information is not available.</p>
          )}
          {!infoLoading && infoExercise !== null && (
            <>
              {infoExercise.description !== null && infoExercise.description.trim().length > 0 && (
                <p className={styles.stateText}>{infoExercise.description}</p>
              )}
              <ExerciseInfoDetails exercise={infoExercise} />
            </>
          )}
        </div>
      )}

      {expanded && (
        <div className={styles.exerciseBody}>
          {target !== null && (
            <p className={styles.cardioTarget}>
              <span className={styles.cardioTargetLabel}>Target</span>
              {target.durationS !== null && <span>{formatDuration(target.durationS)}</span>}
              {target.distanceM !== null && <span>{(target.distanceM / 1000).toFixed(2)} km</span>}
            </p>
          )}

          <ul className={styles.setList}>
            {segments.map((segment, position) => (
              <CardioSegmentRow
                key={segment.id}
                segment={segment}
                index={position}
                machineKind={machineKind}
                onChange={(values) => void handleChange(segment.id, values)}
                onComplete={(values) => void handleComplete(segment.id, values)}
                onUncomplete={() => void handleUncomplete(segment.id)}
              />
            ))}
          </ul>

          {/* Totals for the exercise. `summariseCardio` counts only completed
              segments, so this reflects work actually done rather than what is
              merely typed into a row. */}
          {summary.completedSegments > 0 && (
            <div className={styles.cardioSummary}>
              <div className={styles.summaryStat}>
                <span>Total duration</span>
                <span>{summary.totalDurationS > 0 ? formatDuration(summary.totalDurationS) : '—'}</span>
              </div>
              <div className={styles.summaryStat}>
                <span>Total distance</span>
                <span>
                  {summary.totalDistanceM > 0 ? `${(summary.totalDistanceM / 1000).toFixed(2)} km` : '—'}
                </span>
              </div>
              <div className={styles.summaryStat}>
                <span>Average pace</span>
                <span>{summary.pace.known ? formatPace(summary.pace.value) : '—'}</span>
              </div>
              <div className={styles.summaryStat}>
                <span>Average heart rate</span>
                <span>{summary.averageHr === null ? '—' : `${String(summary.averageHr)} bpm`}</span>
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.addSetButton}
              onClick={() => void handleAddSegment()}
            >
              + Add segment
            </button>
            <button
              type="button"
              className={styles.addSetButton}
              aria-label={`Remove exercise ${String(index + 1)}`}
              onClick={onRemove}
            >
              Remove exercise
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
